import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import helpers from "../lib/helpers";
import { db } from "../lib";
import { get } from "idb-keyval";

const defaultProgress = {
    groupedUnexpectedAsset: 0,
    groupedAssignedAsset: 0,
    groupedComplete: false,
    groupedRequired: 0,
};

// dataSource : "db" || "store"
export default function useGroupAuditProgressDetails(
    dataSource: string,
    auditId: number
) {
    const { audits, altAssets, auditAssets, typesList } = useSelector(
        (state) => state
    );

    const [progress, setProgress] = useState(defaultProgress);

    let auditAltAssets = altAssets?.data;
    let requiredAssets = auditAssets?.data;
    let assetAuditors = [];

    useEffect(() => {
        let isProgressCalled = true;
        const processProgress = async () => {
            let audit = helpers.list.getItemForId(audits, auditId);

            if (dataSource === "db") {
                auditAltAssets = (await get(db.types.altAsset)) || [];
                requiredAssets = (await get(db.types.auditAsset)) || [];
                assetAuditors = (await get(db.types.assetAuditor)) || [];
            }

            if (audit.auditReference && !isNaN(audit.auditReference)) {
                if (Object.keys(audit).length > 0) {
                    const auditRef = parseInt(audit.auditReference);
                    auditAltAssets = auditAltAssets.filter(
                        (item) => item.audit.id === parseInt(auditId)
                    );

                    /**
                     * 1. Filter out assetAuditor for the audit
                     */
                    assetAuditors = assetAuditors.filter(
                        (item) =>
                            parseInt(
                                item.auditAssignment.auditLocation.audit
                                    .auditReference
                            ) === parseInt(auditRef)
                    );

                    const auditedAssets = requiredAssets.filter(
                        (item) =>
                            parseInt(
                                item.auditLocation.audit.auditReference
                            ) === parseInt(auditRef) && item.isAssigned
                    );

                    const getVin = (assetId) => {
                        const asset = auditedAssets.find(
                            (item) => item.id === assetId
                        );
                        return asset ? asset.assetCore.identifier1 : null;
                    };

                    /** Get the summary of the audit
                     * 1. Seen assets
                     * 2. Excluded asset
                     *  a. Get the vin of the excluded asset
                     *  b. Get the exclusion reason of the asset
                     */

                    let excludedAsset = 0,
                        seenAsset = 0,
                        excludedAssetList = [],
                        assetAudited = [];

                    assetAuditors.map((item) => {
                        if (!assetAudited.includes(item.auditAsset.id))
                            assetAudited.push(item.auditAsset.id);
                        if (item.exclusionReason) {
                            excludedAssetList.push({
                                vin: getVin(item.auditAsset.id),
                                exclusionReason: helpers.list.getItemForId(
                                    typesList.exclusionReasonTypes,
                                    item.exclusionReason
                                ).name,
                            });
                            excludedAsset++;
                        } else {
                            seenAsset++;
                        }
                    });

                    requiredAssets = requiredAssets.filter(
                        (item) =>
                            parseInt(
                                item.auditLocation.audit.auditReference
                            ) === parseInt(auditRef) &&
                            item.isMandatory &&
                            item.isExpected &&
                            !item.isAssigned
                    );

                    /**  Filter out
                     * 1. Mandatory assets  and Expected assets
                     * 2. Assigned items false
                     * Should  consider only mandatory assets as we only want them in the list
                     */

                    const assignedAssets = requiredAssets.filter(
                        (i) =>
                            parseInt(i.auditLocation.audit.auditReference) ===
                                parseInt(auditRef) &&
                            i.isAssigned &&
                            i.isMandatory &&
                            i.isExpected
                    );

                    setProgress({
                        groupedUnexpectedAsset: auditAltAssets.length,
                        groupAuditStarted: excludedAsset + seenAsset > 0,
                        groupedAssignedAsset: auditedAssets.length,
                        groupedSeenAsset: seenAsset,
                        groupedExcludedAsset: excludedAsset,
                        groupedExcludedAssetList: excludedAssetList,
                        groupedComplete:
                            requiredAssets.length - assignedAssets.length === 0,
                        groupedRequired: requiredAssets.length,
                    });
                }
            }
        };

        if (isProgressCalled) processProgress();

        // cleanup
        return () => {
            isProgressCalled = false;
        };
    }, [auditId, altAssets, auditAssets]);

    return progress;
}

/** TODO
 * 1. Cleanup
 * 2. Check for common things
 * 3. Audit Reference test case
 */
